import { edgeColumns } from './edgeColumns.ts';
import type { SupabaseLikeClient } from './coachInfluenceContext.ts';
// A narrow SDK operation view avoids coupling shared/browser data to provider SDK generations.
interface PackQuery extends PromiseLike<{data:unknown;error:{message?:string}|null}>{
 eq(column:string,value:unknown):PackQuery;
 order(column:string,options:{ascending:boolean}):PackQuery;
 maybeSingle():PromiseLike<{data:unknown;error:{message?:string}|null}>;
}
interface PackTable {select(columns:string):PackQuery;}


export interface DbFrameworkPackQuestion {
  id: string;
  packId: string;
  lesson: string | null;
  stage: string;
  depth: string;
  modality: string;
  question: string;
  sourceNote: string | null;
}

export interface DbFrameworkPackNoticingSignal {
  id: string;
  label: string;
  description: string;
  positiveIndicators: string[];
  negativeIndicators: string[];
  evidenceHints: string[];
  prohibitedInferences: string[];
}

export interface DbFrameworkPackReflectionPattern {
  id: string;
  label: string;
  promptTemplates: string[];
  useWhen: string[];
  avoidWhen: string[];
}

export interface DbFrameworkPackBoundary {
  allowed: string[];
  prohibited: string[];
  coachOnly: string[];
}

export interface DbFrameworkPackDecisionNode {
  state: string;
  preconditions: string[];
  move: string;
  exitCriteria: string[];
}

export interface DbFrameworkPack {
  /** SHA-256 of current catalog and admitted question-bank content, including native catalog revision. */
  contentVersion?: string;
  catalogRevision?: string | null;
  id: string;
  label: string;
  family: string;
  source: string;
  usageMode: string;
  trademarkNotice: string | null;
  authors: Array<{ name: string; role: string; affiliation: string }>;
  primaryTexts: Array<{ title: string; year?: number; publisher?: string; isbn?: string; note?: string }>;
  theoryOfChange: string | null;
  antiPatternCorrected: string | null;
  philosophySummary: string;
  whatItNotices: string[];
  watchouts: string[];
  sampleCoachLanguage: string[];
  sampleLeaderLanguage: string[];
  coreConstructs: string[];
  diagnosticQuestions: string[];
  interventionPatterns: string[];
  progressMarkers: string[];
  stallSignals: string[];
  noticingSignals: DbFrameworkPackNoticingSignal[];
  reflectionPatterns: DbFrameworkPackReflectionPattern[];
  boundaries: DbFrameworkPackBoundary;
  salienceWeights: Record<string, number>;
  decisionTree: DbFrameworkPackDecisionNode[];
  crossPackLinks: Array<{ packId: string; relationship: string; note?: string }>;
  questionBank?: DbFrameworkPackQuestion[];
}

function parseJsonField<T>(value: unknown, fallback: T): T {
  if (value === null || value === undefined) return fallback;
  if (typeof value === "object") return value as T;
  if (typeof value === "string") {
    try { return JSON.parse(value) as T; } catch { return fallback; }
  }
  return fallback;
}

function mapRow(row: Record<string, unknown>): DbFrameworkPack {
  return {
    id: row.id as string,
    label: row.label as string,
    family: (row.family as string) ?? "veera_core",
    source: (row.source as string) ?? "curated",
    usageMode: (row.usage_mode as string) ?? "public_concepts_only",
    trademarkNotice: (row.trademark_notice as string | null) ?? null,
    authors: parseJsonField(row.authors, []),
    primaryTexts: parseJsonField(row.primary_texts, []),
    theoryOfChange: (row.theory_of_change as string | null) ?? null,
    antiPatternCorrected: (row.anti_pattern_corrected as string | null) ?? null,
    philosophySummary: (row.philosophy_summary as string) ?? "",
    whatItNotices: parseJsonField(row.what_it_notices, []),
    watchouts: parseJsonField(row.watchouts, []),
    sampleCoachLanguage: parseJsonField(row.sample_coach_language, []),
    sampleLeaderLanguage: parseJsonField(row.sample_leader_language, []),
    coreConstructs: parseJsonField(row.core_constructs, []),
    diagnosticQuestions: parseJsonField(row.diagnostic_questions, []),
    interventionPatterns: parseJsonField(row.intervention_patterns, []),
    progressMarkers: parseJsonField(row.progress_markers, []),
    stallSignals: parseJsonField(row.stall_signals, []),
    noticingSignals: parseJsonField(row.noticing_signals, []),
    reflectionPatterns: parseJsonField(row.reflection_patterns, []),
    boundaries: parseJsonField(row.boundaries, { allowed: [], prohibited: [], coachOnly: [] }),
    salienceWeights: parseJsonField(row.salience_weights, {}),
    decisionTree: parseJsonField(row.decision_tree, []),
    crossPackLinks: parseJsonField(row.cross_pack_links, []),
  };
}

function mapQuestionRow(row: Record<string, unknown>): DbFrameworkPackQuestion {
  return {
    id: row.id as string,
    packId: row.pack_id as string,
    lesson: (row.lesson as string | null) ?? null,
    stage: row.stage as string,
    depth: row.depth as string,
    modality: row.modality as string,
    question: row.question as string,
    sourceNote: (row.source_note as string | null) ?? null,
  };
}

export async function fetchFrameworkPackById(
  supabase: SupabaseLikeClient,
  packId: string,
  includeQuestionBank = false,
  options: { throwOnError?: boolean } = {},
): Promise<DbFrameworkPack | null> {
  const { data, error } = await (supabase.from("framework_pack_catalog") as PackTable)
    .select(edgeColumns("framework_pack_catalog", ["id", "label", "family", "source", "usage_mode", "trademark_notice", "authors", "primary_texts", "theory_of_change", "anti_pattern_corrected", "philosophy_summary", "what_it_notices", "watchouts", "sample_coach_language", "sample_leader_language", "core_constructs", "diagnostic_questions", "intervention_patterns", "progress_markers", "stall_signals", "noticing_signals", "reflection_patterns", "boundaries", "salience_weights", "decision_tree", "cross_pack_links", "updated_at"]))
    .eq("id", packId)
    .maybeSingle();

  if (error) {
    console.warn("[frameworkPackDb] framework_pack_query_failed");
    if (options.throwOnError) throw new Error("framework_pack_query_failed");
    return null;
  }
  if (!data) return null;
  const pack = mapRow(data as Record<string, unknown>);
  const strings=(value:unknown)=>Array.isArray(value)&&value.length<=200&&value.every(item=>typeof item==='string'&&item.length<=10000);
  const textFields=['whatItNotices','watchouts','sampleCoachLanguage','sampleLeaderLanguage','coreConstructs','diagnosticQuestions','interventionPatterns','progressMarkers','stallSignals'] as const;
  if(typeof pack.id!=='string'||typeof pack.label!=='string'||typeof pack.philosophySummary!=='string'
    ||textFields.some(field=>!strings(pack[field]))||!Array.isArray(pack.noticingSignals)||pack.noticingSignals.length>200
    ||pack.noticingSignals.some(row=>!row||typeof row.id!=='string'||typeof row.label!=='string'||!strings(row.positiveIndicators)||!strings(row.negativeIndicators)||!strings(row.evidenceHints)||!strings(row.prohibitedInferences))
    ||!Array.isArray(pack.reflectionPatterns)||pack.reflectionPatterns.length>200
    ||pack.reflectionPatterns.some(row=>!row||typeof row.id!=='string'||!strings(row.promptTemplates)||!strings(row.useWhen)||!strings(row.avoidWhen))){
    if(options.throwOnError)throw new Error('framework_pack_invalid_payload');return null;
  }

  if (includeQuestionBank) {
    const { data: questions, error: questionsError } = await (supabase.from("framework_pack_question_bank") as PackTable)
      .select(edgeColumns("framework_pack_question_bank", ["id", "pack_id", "lesson", "stage", "depth", "modality", "question", "source_note"]))
      .eq("pack_id", packId)
      // Equal-stage questions must not reshuffle an otherwise current memory proof.
      .order("stage", { ascending: true })
      .order("id", { ascending: true });
    if (questionsError) {
      console.warn("[frameworkPackDb] framework_questions_query_failed");
      if (options.throwOnError) throw new Error("framework_questions_query_failed");
      return null;
    }
    if (questions) {
      if(!Array.isArray(questions)||questions.length>2000||questions.some(row=>!row||typeof row.id!=='string'||row.pack_id!==packId||typeof row.question!=='string'||row.question.length>10000)){
        if(options.throwOnError)throw new Error('framework_questions_invalid_payload');return null;
      }
      pack.questionBank = (questions as Array<Record<string, unknown>>).map(mapQuestionRow);
    }
  }

  pack.catalogRevision = typeof (data as Record<string, unknown>).updated_at === 'string'
    ? (data as Record<string, unknown>).updated_at as string : null;
  pack.contentVersion = await frameworkPackContentVersion(pack);
  return pack;
}

/** Deterministic across key/query order; any current question/content edit changes the binding. */
export async function frameworkPackContentVersion(pack: DbFrameworkPack): Promise<string> {
  const stable = (value: unknown): unknown => Array.isArray(value) ? value.map(stable)
    : value && typeof value === 'object' ? Object.fromEntries(Object.entries(value).filter(([key])=>key!=='contentVersion').sort(([a],[b])=>a.localeCompare(b)).map(([key,val])=>[key,stable(val)])) : value;
  const canonical = {...pack, questionBank:pack.questionBank ? [...pack.questionBank].sort((a,b)=>a.id.localeCompare(b.id)) : undefined};
  const bytes = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(JSON.stringify(stable(canonical))));
  return [...new Uint8Array(bytes)].map(byte=>byte.toString(16).padStart(2,'0')).join('');
}

export function buildPackSalienceMap(pack: DbFrameworkPack | null): Record<string, number> {
  return pack?.salienceWeights ?? {};
}

export function samplePackQuestions(
  pack: DbFrameworkPack,
  opts: { stage?: string; depth?: string; modality?: string; count?: number } = {},
): string[] {
  const bank = pack.questionBank ?? [];
  const filtered = bank.filter((q) =>
    (!opts.stage || q.stage === opts.stage) &&
    (!opts.depth || q.depth === opts.depth) &&
    (!opts.modality || q.modality === opts.modality)
  );
  const arr = [...filtered].sort((a,b)=>a.id.localeCompare(b.id));
  return arr.slice(0, opts.count ?? 3).map((q) => q.question);
}
